import React, { useEffect, useMemo, useState } from "react";
import styles from "./editProduct.module.css";
import * as v from "valibot";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Col, Container, Form, Image, Row } from "react-bootstrap";
import { PageHeading } from "../../../components/common/pageHeading/PageHeading";
import productImgPlaceholder from "../../../assests/images/placeholder_image.webp";
import { useForm } from "react-hook-form";
import { Input } from "../../../components/common/input/Input";
import { ReadOnlyInput } from "../../../components/common/input/ReadOnlyInput";
import { Select } from "../../../components/common/select/Select";
import {
  useGetProductQuery,
  useUpdateProductMutation,
} from "../../../features/api/products/productsApi";
import dateFormatter from "../../../utils/dateFormatter";
import { CustomToast } from "../../../components/common/customToast/CustomToast";
import { Loader } from "../../../components/common/loader/Loader";
import { useIsSeniorSalesExecutive } from "../../../customHooks/roleChecker/useIsSeniorSalesExecutive";
const schema = v.object({
  name: v.pipe(
    v.string("The product name is required"),
    v.minLength(3, "Must be at least 3 characters long"),
  ),
  size: v.pipe(
    v.string("The product size is required"),
    v.minLength(2, "Must be at least 2 characters long"),
  ),
  intakePrice: v.pipe(
    v.number("The purchase price is required"),
    v.minValue(0.01, "The price must be greater than zero"),
  ),
  sellingPrice: v.pipe(
    v.number("The selling price is required"),
    v.minValue(0.01, "The price must be greater than zero"),
  ),
  unit: v.pipe(
    v.number("The unit/pack is required"),
    v.minValue(1, "Must be at least 1"),
  ),
  weight: v.pipe(
    v.number("Stock is required"),
    v.minValue(1, "Stock cannot be negative"),
  ),
  manufacturer: v.string("The manufacturer is required"),
  origin: v.string("The country of origin is required"),
  category: v.pipe(
    v.string("Category is required"),
    v.minLength(2, "Category is required"),
  ),
  dimension: v.string("The dimension is required"),
});

export const EditProduct = () => {
  const isSeniorSalesExecutive = useIsSeniorSalesExecutive();
  const [updateProduct, { isLoading, isSuccess, isError, error }] =
    useUpdateProductMutation();
  const navigate = useNavigate();
  const { productId } = useParams();
  const { data: productDetail } = useGetProductQuery(productId);
  const [productImage, setProductImage] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: productDetail,
    resolver: valibotResolver(schema),
    mode: "onChange",
  });
  useEffect(() => {
    if (productDetail) {
      reset(productDetail); // Reset the entire form with new product data
    }
  }, [productDetail, reset]);

  const onSubmit = async (data) => {
    const productData = new FormData();
    productData.append(
      "productRequestDto",
      JSON.stringify({
        id: productId,
        name: data.name,
        unit: data.unit,
        origin: data.country,
        category: data.category,
        dimension: data.dimension,
        manufacturer: data.manufacturer,
        weight: data.weight,
        intakePrice: data.intakePrice,
        sellingPrice: data.sellingPrice,
        size: data.size,
      }),
    );

    if (data.image && data.image.length > 0) {
      productData.append("image", data.image[0]); // Extract the first file
    }
    try {
      await updateProduct(productData).unwrap();

      setTimeout(() => {
        navigate("/inventory");
      }, 2000);
    } catch (err) {}
    // Perform API request to update product -
    reset();
  };
  let content = null;
  if (isLoading) {
    content = (
      <div className="mb-5">
        <PageHeading title="Edit Product" />
        <Loader />
      </div>
    );
  }
  if (productDetail) {
    content = (
      <div className="mb-5">
        {isSuccess && <CustomToast msg={"Product updated successfully!"} />}
        {isError && <CustomToast err={error} msg={"Something went wrong"} />}
        <PageHeading title="Edit Product" />
        <Container className="mt-5 shadow-sm">
          <Row className="my-5">
            <Col lg={12} xs={12} md={12} className="text-center my-2">
              <Image
                className={styles.productImage}
                fluid
                src={
                  `data:image/webp;base64,${productDetail?.image}` ||
                  productImgPlaceholder
                }
              />
            </Col>
          </Row>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col lg={6} md={6} xs={12}>
                {isSeniorSalesExecutive ? (
                  <ReadOnlyInput label={"Name"} value={productDetail?.name} />
                ) : (
                  <Input
                    label={"Name"}
                    field={"name"}
                    register={register}
                    errors={errors}
                  />
                )}
                {isSeniorSalesExecutive ? (
                  <ReadOnlyInput
                    label={"Name"}
                    value={productDetail?.intakePrice}
                  />
                ) : (
                  <Input
                    label={"Intake Price"}
                    type={"number"}
                    withUnit={true}
                    field={"intakePrice"}
                    register={register}
                    errors={errors}
                  />
                )}
                <Input
                  label={"Selling Price"}
                  type={"number"}
                  withUnit={true}
                  field={"sellingPrice"}
                  register={register}
                  errors={errors}
                />
                {isSeniorSalesExecutive ? (
                  <ReadOnlyInput
                    label={"Unit/Pack"}
                    value={productDetail?.unit}
                  />
                ) : (
                  <Input
                    label={"Unit/Pack"}
                    type={"number"}
                    field={"unit"}
                    register={register}
                    errors={errors}
                  />
                )}
                <ReadOnlyInput
                  label={"Created At"}
                  value={dateFormatter(productDetail?.createdAt)}
                />
                <ReadOnlyInput
                  label={"Updated At"}
                  value={dateFormatter(
                    productDetail?.productLogResponses[
                      productDetail.productLogResponses.length - 1
                    ]?.updatedAt,
                  )}
                />
                <ReadOnlyInput
                  label={"Updated By"}
                  value={
                    productDetail?.productLogResponses[
                      productDetail.productLogResponses.length - 1
                    ]?.updatedBy
                  }
                />
              </Col>

              <Col lg={6} md={6} xs={12}>
                {isSeniorSalesExecutive ? (
                  <ReadOnlyInput label={"Size"} value={productDetail?.size} />
                ) : (
                  <Input
                    label={"Size"}
                    field={"size"}
                    register={register}
                    errors={errors}
                  />
                )}
                {isSeniorSalesExecutive ? (
                  <ReadOnlyInput
                    label={"Size"}
                    value={productDetail?.manufacturer}
                  />
                ) : (
                  <Input
                    label={"Manufacturer"}
                    field={"manufacturer"}
                    register={register}
                    errors={errors}
                  />
                )}
                {isSeniorSalesExecutive ? (
                  <ReadOnlyInput label={"Size"} value={productDetail?.origin} />
                ) : (
                  <Input
                    label={"Origin"}
                    field={"origin"}
                    register={register}
                    errors={errors}
                  />
                )}
                {isSeniorSalesExecutive ? (
                  <ReadOnlyInput label={"Size"} value={productDetail?.weight} />
                ) : (
                  <Input
                    label={"Weight"}
                    field={"weight"}
                    type={"number"}
                    register={register}
                    errors={errors}
                  />
                )}
                {isSeniorSalesExecutive ? (
                  <ReadOnlyInput
                    label={"Size"}
                    value={productDetail?.dimension}
                  />
                ) : (
                  <Input
                    label={"Dimension"}
                    field={"dimension"}
                    register={register}
                    errors={errors}
                  />
                )}

                {isSeniorSalesExecutive ? (
                  <ReadOnlyInput
                    label={"Size"}
                    value={productDetail?.category}
                  />
                ) : (
                  <Select
                    label="Category"
                    field="category"
                    register={register}
                    options={[
                      { value: "House hold", label: "House Hold" },
                      { value: "Electronics", label: "Electronics" },
                      { value: "Clothing", label: "Clothing" },
                    ]}
                    defaultValue={productDetail?.category}
                    errors={errors}
                  />
                )}
                {!isSeniorSalesExecutive && (
                  <Form.Group className="mb-3">
                    <Form.Label>Image</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      {...register("image", { required: "Image is required" })}
                      onChange={(e) =>
                        setProductImage({
                          ...productDetail,
                          image: e.target.files[0],
                        })
                      }
                    />
                    {errors.image && (
                      <p className="text-danger">{errors.image.message}</p>
                    )}
                  </Form.Group>
                )}
              </Col>
            </Row>

            <Row className="py-2 d-flex justify-content-center g-2">
              <Col lg={2}>
                <Button
                  type="button"
                  variant="secondary"
                  className="w-100"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
              </Col>
              <Col lg={2}>
                <Button type="submit" variant="success" className="w-100">
                  Save Changes
                </Button>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
    );
  }

  return content;
};
